import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable, Subject } from 'rxjs';
import { debounceTime, filter, map, take } from 'rxjs/operators';

import { ArmyBuilderConfig } from '../../global/config';
import { HttpClientWithInFlightCache } from '../../global/httpClient';
import { SettingsService } from '../../global/settings/service/settings.service';
import { Force } from '../models';
import {
    ForceFolder,
    ForceOrganisationDocument,
    ForceOrganisationItem,
    ForceOrganisationViewMode,
    assignForcesToFolder,
    createBootstrapDocument
} from './models';
import { clearForceOrganisation, setFolders, setForceOrganisation, setItems, setViewMode } from './state/actions';
import { selectFolders, selectForceOrganisation, selectOrgItems, selectViewMode } from './state/selectors';

@Injectable()
export class ForceOrganisationService {
    private saveSubject = new Subject<{ gameId: string; doc: ForceOrganisationDocument }>();

    constructor(
        private store: Store<any>,
        private httpClient: HttpClientWithInFlightCache,
        private config: ArmyBuilderConfig,
        private settingsService: SettingsService
    ) {
        this.saveSubject.pipe(debounceTime(2000)).subscribe(({ gameId, doc }) => {
            this.persist(gameId, doc);
        });
    }

    viewMode$(gameId: string): Observable<ForceOrganisationViewMode> {
        return this.store.select(selectViewMode(gameId));
    }

    folders$(gameId: string): Observable<ForceFolder[]> {
        return this.store.select(selectFolders(gameId));
    }

    items$(gameId: string): Observable<ForceOrganisationItem[]> {
        return this.store.select(selectOrgItems(gameId));
    }

    async load(gameId: string): Promise<void> {
        const url = `${this.config.apiBaseUrl}/userData/force-organization/${gameId}`;
        const headers = this.config.globalRequestHeaders;
        this.httpClient
            .get<ForceOrganisationDocument | null>(url, { headers, requiresLogin: true })
            .pipe(filter((doc) => !!doc))
            .subscribe((doc) => {
                this.store.dispatch(setForceOrganisation({ doc: doc! }));
            });
    }

    setItemsDirectly(gameId: string, items: ForceOrganisationItem[]): void {
        this.store.dispatch(setItems({ gameId, items }));
        this.getDoc(gameId).then((doc) => this.queueSave(gameId, doc));
    }

    setItemsAndFoldersDirectly(gameId: string, items: ForceOrganisationItem[], folders: ForceFolder[]): void {
        this.store.dispatch(setItems({ gameId, items }));
        this.store.dispatch(setFolders({ gameId, folders }));
        this.getDoc(gameId).then((doc) => this.queueSave(gameId, doc));
    }

    setViewMode(gameId: string, mode: ForceOrganisationViewMode, ownForces: Force[]): void {
        this.store
            .select(selectForceOrganisation(gameId))
            .pipe(take(1))
            .subscribe((existing) => {
                if (existing) {
                    this.store.dispatch(setViewMode({ gameId, mode }));
                    this.getDoc(gameId).then((doc) => this.queueSave(gameId, doc));
                } else {
                    // Not in store yet — re-fetch to distinguish a race condition from a genuine new user.
                    // HttpClientWithInFlightCache deduplicates if load() is still in-flight.
                    const url = `${this.config.apiBaseUrl}/userData/force-organization/${gameId}`;
                    this.httpClient
                        .get<ForceOrganisationDocument | null>(url, { headers: this.config.globalRequestHeaders, requiresLogin: true })
                        .pipe(take(1))
                        .subscribe((doc) => {
                            if (doc) {
                                const updated = { ...doc, viewMode: mode };
                                this.store.dispatch(setForceOrganisation({ doc: updated }));
                                this.queueSave(gameId, updated);
                            } else {
                                const userId = this.getCurrentUserId();
                                const newDoc = createBootstrapDocument(gameId, userId, ownForces);
                                newDoc.viewMode = mode;
                                this.store.dispatch(setForceOrganisation({ doc: newDoc }));
                                this.queueSave(gameId, newDoc);
                            }
                        });
                }
            });
    }

    reorderItems(gameId: string, fromIndex: number, toIndex: number, scope: 'root' | string): void {
        this.store
            .select(selectOrgItems(gameId))
            .pipe(take(1))
            .subscribe((allItems) => {
                const scopeItems =
                    scope === 'root' ? allItems.filter((i) => i.folderId === null) : allItems.filter((i) => i.folderId === scope);
                const otherItems = allItems.filter((i) => !scopeItems.includes(i));

                const reordered = [...scopeItems];
                const [moved] = reordered.splice(fromIndex, 1);
                reordered.splice(toIndex, 0, moved);
                const renumbered = reordered.map((item, idx) => ({ ...item, sortOrder: idx * 1000 }));

                const newItems = [...otherItems, ...renumbered].sort((a, b) => a.sortOrder - b.sortOrder);
                this.store.dispatch(setItems({ gameId, items: newItems }));
                this.getDoc(gameId).then((doc) => this.queueSave(gameId, doc));
            });
    }

    createFolder(gameId: string, name: string): void {
        const id = this.generateId();
        this.store
            .select(selectFolders(gameId))
            .pipe(take(1))
            .subscribe((folders) => {
                const newFolders = [...folders, { id, name, parentId: null }];
                this.store.dispatch(setFolders({ gameId, folders: newFolders }));
                this.getDoc(gameId).then((doc) => this.queueSave(gameId, doc));
            });
    }

    renameFolder(gameId: string, folderId: string, name: string): void {
        this.store
            .select(selectFolders(gameId))
            .pipe(take(1))
            .subscribe((folders) => {
                const newFolders = folders.map((f) => (f.id === folderId ? { ...f, name } : f));
                this.store.dispatch(setFolders({ gameId, folders: newFolders }));
                this.getDoc(gameId).then((doc) => this.queueSave(gameId, doc));
            });
    }

    deleteFolder(gameId: string, folderId: string): void {
        this.store
            .select(selectOrgItems(gameId))
            .pipe(take(1))
            .subscribe((items) => {
                const updatedItems = items.map((item) => (item.folderId === folderId ? { ...item, folderId: null } : item));
                this.store.dispatch(setItems({ gameId, items: updatedItems }));
                this.store
                    .select(selectFolders(gameId))
                    .pipe(take(1))
                    .subscribe((folders) => {
                        this.store.dispatch(setFolders({ gameId, folders: folders.filter((f) => f.id !== folderId) }));
                        this.getDoc(gameId).then((doc) => this.queueSave(gameId, doc));
                    });
            });
    }

    /**
     * Bulk twin of moveForceToFolder, for the "Move Multiple" selection mode.
     * Deliberately not a loop over moveForceToFolder: that would take a fresh
     * store snapshot per force (so each move would compute its sortOrder from a
     * stale destination) and queue a separate debounced save each time. One
     * snapshot, one dispatch, one save.
     */
    moveForcesToFolder(gameId: string, forceIds: string[], type: 'own' | 'shared', folderId: string | null): void {
        if (!forceIds?.length) {
            return;
        }
        this.store
            .select(selectOrgItems(gameId))
            .pipe(take(1))
            .subscribe((items) => {
                const newItems = assignForcesToFolder(items, forceIds, type, folderId);
                this.store.dispatch(setItems({ gameId, items: newItems }));
                this.getDoc(gameId).then((doc) => this.queueSave(gameId, doc));
            });
    }

    moveForceToFolder(gameId: string, forceId: string, type: 'own' | 'shared', folderId: string | null): void {
        this.store
            .select(selectOrgItems(gameId))
            .pipe(take(1))
            .subscribe((items) => {
                const exists = items.some((i) => i.forceId === forceId);
                // Place at end of destination scope (folder or root)
                const scopeItems = items.filter((i) => i.folderId === folderId && i.forceId !== forceId);
                const maxScopeOrder = scopeItems.reduce((max, i) => Math.max(max, i.sortOrder), -1000);
                const newSortOrder = maxScopeOrder + 1000;
                let newItems: ForceOrganisationItem[];
                if (exists) {
                    newItems = items.map((i) => (i.forceId === forceId ? { ...i, folderId, sortOrder: newSortOrder } : i));
                } else {
                    newItems = [...items, { forceId, type, folderId, sortOrder: newSortOrder }];
                }
                this.store.dispatch(setItems({ gameId, items: newItems }));
                this.getDoc(gameId).then((doc) => this.queueSave(gameId, doc));
            });
    }

    addSharedForce(gameId: string, forceId: string): void {
        this.moveForceToFolder(gameId, forceId, 'shared', null);
    }

    removeSharedForceFromOrg(gameId: string, forceId: string): void {
        this.store
            .select(selectOrgItems(gameId))
            .pipe(take(1))
            .subscribe((items) => {
                const newItems = items.filter((i) => !(i.forceId === forceId && i.type === 'shared'));
                this.store.dispatch(setItems({ gameId, items: newItems }));
                this.getDoc(gameId).then((doc) => this.queueSave(gameId, doc));
            });
    }

    private queueSave(gameId: string, doc: ForceOrganisationDocument): void {
        this.saveSubject.next({ gameId, doc });
    }

    private async getDoc(gameId: string): Promise<ForceOrganisationDocument> {
        return this.store.select(selectForceOrganisation(gameId)).pipe(take(1)).toPromise() as Promise<ForceOrganisationDocument>;
    }

    private persist(gameId: string, doc: ForceOrganisationDocument): void {
        const url = `${this.config.apiBaseUrl}/userData/force-organization/${gameId}`;
        const headers = this.config.globalRequestHeaders;
        this.httpClient
            .put<number>(url, doc, { headers, requiresLogin: true })
            .subscribe({ error: (err) => console.error('[ForceOrganisation] save failed', err) });
    }

    private getCurrentUserId(): string {
        return this.settingsService.settings()?.global?.login?.user?.userId ?? 'unknown';
    }

    private generateId(): string {
        return Math.random().toString(36).slice(2) + Date.now().toString(36);
    }
}
